// Calculs spatiaux simples (D-031) : point dans polygone par l'algorithme du rayon, distance géodésique,
// projection équirectangulaire vers les coordonnées pixel de la carte des provinces, normalisation de noms.

/** Position en longitude, latitude (degrés décimaux). */
export type Position = [number, number];
/** Polygone GeoJSON : anneau extérieur puis anneaux intérieurs éventuels. */
export type Polygone = Position[][];
export type MultiPolygone = Position[][][];

function pointDansAnneau(point: Position, anneau: Position[]): boolean {
  const [x, y] = point;
  let dedans = false;
  for (let i = 0, j = anneau.length - 1; i < anneau.length; j = i++) {
    const [xi, yi] = anneau[i] as Position;
    const [xj, yj] = anneau[j] as Position;
    const traverse = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (traverse) dedans = !dedans;
  }
  return dedans;
}

/** Vrai si le point est dans l'anneau extérieur et hors de tout anneau intérieur. */
export function pointDansPolygone(point: Position, polygone: Polygone): boolean {
  const [exterieur, ...trous] = polygone;
  if (!exterieur || !pointDansAnneau(point, exterieur)) return false;
  return !trous.some((trou) => pointDansAnneau(point, trou));
}

export function pointDansGeometrie(
  point: Position,
  geometrie: { type: 'Polygon'; coordinates: Polygone } | { type: 'MultiPolygon'; coordinates: MultiPolygone },
): boolean {
  if (geometrie.type === 'Polygon') return pointDansPolygone(point, geometrie.coordinates);
  return geometrie.coordinates.some((p) => pointDansPolygone(point, p));
}

const RAYON_TERRE_METRES = 6_371_008.8;

/** Distance de grand cercle (formule de haversine), en mètres. */
export function distanceMetres(a: Position, b: Position): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLon = (b[0] - a[0]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * RAYON_TERRE_METRES * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface EmpriseCarte {
  viewBox: [number, number, number, number];
  bbox: { lonW: number; lonE: number; latS: number; latN: number };
}

/** Projection équirectangulaire identique à celle de la vectorisation des provinces. */
export function projeterVersPixels(position: Position, emprise: EmpriseCarte): [number, number] {
  const [x0, y0, x1, y1] = emprise.viewBox;
  const { lonW, lonE, latS, latN } = emprise.bbox;
  const x = x0 + ((position[0] - lonW) / (lonE - lonW)) * (x1 - x0);
  const y = y0 + ((latN - position[1]) / (latN - latS)) * (y1 - y0);
  return [x, y];
}

/** Nom normalisé : minuscules, sans accents ni ponctuation, espaces simples. */
export function normaliserNom(nom: string): string {
  return nom
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Deux noms sont proches si leurs formes normalisées sont égales ou si l'une contient l'autre. */
export function nomsProches(a: string, b: string): boolean {
  const na = normaliserNom(a);
  const nb = normaliserNom(b);
  if (!na || !nb) return false;
  return na === nb || (na.length >= 4 && nb.includes(na)) || (nb.length >= 4 && na.includes(nb));
}

export function coordonneesValides(lon: number, lat: number): boolean {
  return Number.isFinite(lon) && Number.isFinite(lat) && lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
}
